import React from 'react';
import { Collapse } from '@mui/material';
import { Alert, DatePicker, Text } from 'library/ui';
import { LabeledField } from './LabeledField';
import { verificationDateChecks } from './verificationDates';

export function EligibleDatesFields({
    workerName,
    determinationDate,
    onDeterminationDateChange,
    printsExpirationDate,
    onPrintsExpirationDateChange,
}: {
    workerName: string;
    determinationDate: string;
    onDeterminationDateChange: (value: string) => void;
    printsExpirationDate: string;
    onPrintsExpirationDateChange: (value: string) => void;
}) {
    const {
        today,
        determinationDateInFuture,
        determinationDateIsRecent,
        printsExpirationDateInPast,
        printsExpiringSoon,
        datesWithinAYear,
    } = verificationDateChecks(determinationDate, printsExpirationDate);

    return (
        <>
            <LabeledField
                label="DCF Child Care Eligibility Determination Date"
                help="Find this on the worker's Clearinghouse record for DCF Child Care, then input the date."
                image="https://cdn.join-tandem.com/media/tandem_static/child_care_eligibility_date_instructions.png"
                required
            >
                <DatePicker
                    value={determinationDate}
                    onChange={onDeterminationDateChange}
                    maxDate={today}
                    fullWidth
                />
                {determinationDateInFuture && (
                    <Text tone="error">
                        The eligibility determination date cannot be in the future. Enter the date shown on the
                        worker&rsquo;s Clearinghouse record.
                    </Text>
                )}
                <Collapse in={determinationDateIsRecent} unmountOnExit>
                    <Alert variant="warning">
                        <Text size="xs">
                            It looks like they became eligible recently. Double check you entered it correctly.
                        </Text>
                    </Alert>
                </Collapse>
            </LabeledField>
            <LabeledField
                label="Retained Prints Expiration Date"
                help="Find the Retained Prints Expiration Date on the worker's Clearinghouse record in the top right corner, then input the date."
                image="https://cdn.join-tandem.com/media/tandem_static/retained_prints_instructions.png"
                required
            >
                <DatePicker
                    value={printsExpirationDate}
                    onChange={onPrintsExpirationDateChange}
                    minDate={today}
                    fullWidth
                />
                {printsExpirationDateInPast && (
                    <Text tone="error">
                        The retained prints expiration date cannot be in the past. If the prints have already expired,{' '}
                        {workerName} is not eligible to book.
                    </Text>
                )}
                <Collapse in={printsExpiringSoon} unmountOnExit>
                    <Alert variant="warning">
                        <Text size="xs">
                            This substitute&apos;s check is expiring soon. Double check you entered it in correctly.
                        </Text>
                    </Alert>
                </Collapse>
            </LabeledField>
            <Collapse in={datesWithinAYear} unmountOnExit>
                <Alert variant="warning">
                    <Text size="xs">
                        These two dates are less than a year apart. Double check you entered both of them correctly.
                    </Text>
                </Alert>
            </Collapse>
        </>
    );
}
